import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { hasFullAccess } from '../lib/permissions';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useVotingData, useRecordsBlocks, useCampaigns } from '../hooks/queries';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Vote, 
  Users, 
  CheckCircle2, 
  Trash2, 
  Edit2, 
  UserCheck,
  Clock,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Link,
  Copy,
  Unlink,
  List,
  UserCircle,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FiyavalhuPage() {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  const canAssignUsers = authUser && (hasFullAccess(authUser) || authUser.effective_permissions?.includes('voting.assign_boxes'));
  const [searchParams] = useSearchParams();
  const constituency_id = searchParams.get('constituency_id');
  const campaign_id = searchParams.get('campaign_id');

  useRegisterSocketRooms(
    campaign_id ? [`voting:${campaign_id}`, `turnout:${campaign_id}`] : []
  );

  const { data: campaigns } = useCampaigns(constituency_id);
  const campaign = campaigns?.find((c) => c.campaign_id === campaign_id);
  const campaignName = campaign?.name ?? '';

  const { boxes, stats, publicLink, isLoading: loading, refetch } = useVotingData(campaign_id);
  const boxesData = boxes.data ?? [];
  const statsData = stats.data ?? null;
  const publicLinkData = publicLink.data ?? { enabled: false };

  const { data: blocksData } = useRecordsBlocks(constituency_id, campaign_id);
  const availableBlocks = blocksData?.blocks ?? [];
  
  // Format time for display (HH:MM -> h:mm AM/PM)
  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditBoxDialog, setShowEditBoxDialog] = useState(false);
  const [showVotersDialog, setShowVotersDialog] = useState(false);
  const [showAssignUsersDialog, setShowAssignUsersDialog] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignUsersSearch, setAssignUsersSearch] = useState('');
  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [allAssignableUsers, setAllAssignableUsers] = useState([]);
  const [boxVoters, setBoxVoters] = useState([]);
  const [boxVotersLoading, setBoxVotersLoading] = useState(false);
  const [boxVotersTotal, setBoxVotersTotal] = useState(0);
  const [boxVotersPage, setBoxVotersPage] = useState(1);
  const [boxVotersSearch, setBoxVotersSearch] = useState('');
  const boxVotersPageSize = 20;
  
  // Form data
  const [newBox, setNewBox] = useState({ name: '', description: '' });
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [checkInId, setCheckInId] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [editBoxData, setEditBoxData] = useState({
    manual_registered: '',
    manual_voted: '',
    manual_pledges_registered: '',
    manual_pledgers_voted: ''
  });

  const invalidateVoting = () => {
    queryClient.invalidateQueries({ queryKey: ['voting'] });
    queryClient.invalidateQueries({ queryKey: ['records'] });
  };

  const handleCreateBox = async () => {
    if (!newBox.name) {
      toast.error('Please enter a box name');
      return;
    }

    try {
      await api.post(`${API}/campaigns/${campaign_id}/voting-boxes`, newBox);
      toast.success('Voting box created');
      setShowCreateDialog(false);
      setNewBox({ name: '', description: '' });
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create box');
    }
  };

  const handleGeneratePublicLink = async () => {
    try {
      const { data } = await api.post(`${API}/campaigns/${campaign_id}/public-link`, {});
      queryClient.invalidateQueries({ queryKey: ['voting', 'publicLink', campaign_id] });
      toast.success('Public link generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    }
  };

  const handleRemovePublicLink = async () => {
    try {
      await api.delete(`${API}/campaigns/${campaign_id}/public-link`);
      queryClient.invalidateQueries({ queryKey: ['voting', 'publicLink', campaign_id] });
      toast.success('Public link removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove link');
    }
  };

  const copyPublicLink = () => {
    const fullUrl = `${window.location.origin}${publicLinkData.public_url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard');
  };

  const handleAssignBlocks = async () => {
    if (!selectedBox || selectedBlocks.length === 0) {
      toast.error('Please select blocks to assign');
      return;
    }

    try {
      const response = await api.post(
        `${API}/voting-boxes/${selectedBox.box_id}/assign-blocks`,
        { blocks: selectedBlocks },
      );
      toast.success(`Assigned ${response.data.constituents_assigned} constituents`);
      setShowAssignDialog(false);
      setSelectedBlocks([]);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign blocks');
    }
  };

  const handleAssignUnassigned = async (boxId) => {
    try {
      const response = await api.post(
        `${API}/voting-boxes/${boxId}/assign-unassigned`,
        {},
      );
      toast.success(`Assigned ${response.data.constituents_assigned} unassigned constituents`);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleDeleteBox = async (boxId) => {
    if (!window.confirm('Delete this voting box? Constituents will be unassigned.')) return;

    try {
      await api.delete(`${API}/voting-boxes/${boxId}`);
      toast.success('Voting box deleted');
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete box');
    }
  };

  const handleCheckIn = async () => {
    const idToUse = checkInId.trim().toUpperCase();
    if (!idToUse) {
      toast.error('Please enter an ID card number');
      return;
    }

    setCheckInLoading(true);
    setCheckInResult(null);

    try {
      const response = await api.post(
        `${API}/voting-boxes/${selectedBox.box_id}/check-in`,
        { id_number: idToUse },
      );
      
      setCheckInResult({
        success: true,
        message: 'Voter checked in successfully!',
        constituent: response.data.constituent
      });
      
      toast.success(`${response.data.constituent.name} checked in!`);
      setCheckInId('');
      invalidateVoting();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Check-in failed';
      if (typeof detail === 'object' && detail !== null) {
        message = detail.message || message;
        if (detail.code === 'wrong_box' && detail.correct_box) {
          message = `This voter is not for this box. Correct box: ${detail.correct_box}`;
        } else if (detail.code === 'not_found') {
          message = 'ID card number not found in this campaign';
        } else if (detail.code === 'already_voted') {
          message = detail.message;
        }
      } else if (typeof detail === 'string') {
        message = detail;
      }
      setCheckInResult({ success: false, message });
    } finally {
      setCheckInLoading(false);
    }
  };

  const openCheckInDialog = (box) => {
    setSelectedBox(box);
    setCheckInId('');
    setCheckInResult(null);
    setShowCheckInDialog(true);
  };

  const openVotersDialog = async (box) => {
    setSelectedBox(box);
    setBoxVotersSearch('');
    setShowVotersDialog(true);
    setBoxVotersPage(1);
    setBoxVotersLoading(true);
    try {
      const params = { page: 1, page_size: boxVotersPageSize };
      if (boxVotersSearch.trim()) params.search = boxVotersSearch.trim();
      const res = await api.get(
        `${API}/voting-boxes/${box.box_id}/constituents`,
        { params, withCredentials: true }
      );
      setBoxVoters(res.data.constituents || []);
      setBoxVotersTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load voters');
      setBoxVoters([]);
      setBoxVotersTotal(0);
    } finally {
      setBoxVotersLoading(false);
    }
  };

  const loadBoxVotersPage = async (page, search) => {
    if (!selectedBox) return;
    const searchParam = (search !== undefined ? search : boxVotersSearch).trim();
    setBoxVotersLoading(true);
    try {
      const params = { page, page_size: boxVotersPageSize };
      if (searchParam) params.search = searchParam;
      const res = await api.get(
        `${API}/voting-boxes/${selectedBox.box_id}/constituents`,
        { params, withCredentials: true }
      );
      setBoxVoters(res.data.constituents || []);
      setBoxVotersTotal(res.data.total || 0);
      setBoxVotersPage(page);
    } catch (e) {
      toast.error('Failed to load voters');
    } finally {
      setBoxVotersLoading(false);
    }
  };

  const handleBoxVotersSearchChange = (value) => {
    setBoxVotersSearch(value);
    if (!selectedBox) return;
    setBoxVotersPage(1);
    loadBoxVotersPage(1, value);
  };

  const openAssignUsersDialog = async (box) => {
    setSelectedBox(box);
    setAssignUsersSearch('');
    setShowAssignUsersDialog(true);
    setAssignUsersLoading(true);
    try {
      const [assignedRes, allUsersRes] = await Promise.all([
        api.get(`${API}/voting-boxes/${box.box_id}/assigned-users`),
        api.get(`${API}/campaigns/${campaign_id}/users-search`, {
          params: { q: '', box_id: box.box_id },
          withCredentials: true
        })
      ]);
      setAssignedUsers(assignedRes.data.users || []);
      setAllAssignableUsers(allUsersRes.data.users || []);
    } catch (e) {
      toast.error('Failed to load users');
      setAssignedUsers([]);
      setAllAssignableUsers([]);
    } finally {
      setAssignUsersLoading(false);
    }
  };

  const openAssignDialog = (box) => {
    setSelectedBox(box);
    setSelectedBlocks(box.assigned_blocks || []);
    setShowAssignDialog(true);
  };

  const openEditBoxDialog = (box) => {
    setSelectedBox(box);
    setEditBoxData({
      manual_registered: box.manual_registered ?? '',
      manual_voted: box.manual_voted ?? '',
      manual_pledges_registered: box.manual_pledges_registered ?? '',
      manual_pledgers_voted: box.manual_pledgers_voted ?? ''
    });
    setShowEditBoxDialog(true);
  };

  const handleAssignUsersSearchChange = (value) => {
    setAssignUsersSearch(value);
  };

  const filteredAssignableUsers = allAssignableUsers.filter((u) => {
    if (u.already_assigned) return false;
    const q = assignUsersSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const handleAssignUser = async (userId) => {
    if (!selectedBox) return;
    try {
      await api.post(
        `${API}/voting-boxes/${selectedBox.box_id}/assign-user?user_id=${userId}`,
        {},
      );
      toast.success('User assigned');
      const res = await api.get(`${API}/voting-boxes/${selectedBox.box_id}/assigned-users`);
      setAssignedUsers(res.data.users || []);
      setAllAssignableUsers(prev => prev.map(u => u.user_id === userId ? { ...u, already_assigned: true } : u));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!selectedBox) return;
    try {
      await api.delete(
        `${API}/voting-boxes/${selectedBox.box_id}/assign-user?user_id=${userId}`,
      );
      toast.success('User removed');
      const res = await api.get(`${API}/voting-boxes/${selectedBox.box_id}/assigned-users`);
      setAssignedUsers(res.data.users || []);
      setAllAssignableUsers(prev => prev.map(u => u.user_id === userId ? { ...u, already_assigned: false } : u));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleUpdateBox = async () => {
    if (!selectedBox) return;

    try {
      const payload = {};
      
      // Only include fields that have values (empty string means use auto-calculated)
      if (editBoxData.manual_registered !== '') {
        payload.manual_registered = parseInt(editBoxData.manual_registered, 10);
      } else {
        payload.manual_registered = -1; // Signal to clear
      }
      
      if (editBoxData.manual_voted !== '') {
        payload.manual_voted = parseInt(editBoxData.manual_voted, 10);
      } else {
        payload.manual_voted = -1;
      }
      
      if (editBoxData.manual_pledges_registered !== '') {
        payload.manual_pledges_registered = parseInt(editBoxData.manual_pledges_registered, 10);
      } else {
        payload.manual_pledges_registered = -1;
      }
      
      if (editBoxData.manual_pledgers_voted !== '') {
        payload.manual_pledgers_voted = parseInt(editBoxData.manual_pledgers_voted, 10);
      } else {
        payload.manual_pledgers_voted = -1;
      }

      await api.put(
        `${API}/voting-boxes/${selectedBox.box_id}`,
        payload,
      );
      toast.success('Voting box counts updated');
      setShowEditBoxDialog(false);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update box');
    }
  };

  const handleResetBoxCounts = async () => {
    if (!selectedBox) return;
    
    if (!window.confirm('Reset to auto-calculated values? This will clear all manual overrides.')) return;

    try {
      await api.post(
        `${API}/voting-boxes/${selectedBox.box_id}/reset-manual`,
        {},
      );
      toast.success('Box counts reset to auto-calculated values');
      setShowEditBoxDialog(false);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset counts');
    }
  };

  const toggleBlock = (block) => {
    setSelectedBlocks(prev => 
      prev.includes(block) 
        ? prev.filter(b => b !== block)
        : [...prev, block]
    );
  };

  if (!campaign_id) {
    return (
      <Layout>
        <div className="p-4 md:p-8 text-center">
          <Vote className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Fiyavalhu - Voting Day Management</h2>
          <p className="text-muted-foreground">Please select a campaign from the Campaigns page to manage voting day.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8" data-testid="fiyavalhu-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fiyavalhu
            </h1>
            <p className="text-muted-foreground">
              Voting Day Management - {campaignName}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <Button variant="outline" onClick={invalidateVoting} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-box-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Voting Box
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Voting Box</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Box Name</Label>
                    <Input
                      value={newBox.name}
                      onChange={(e) => setNewBox({ ...newBox, name: e.target.value })}
                      placeholder="e.g., Box 1, Kulhudhuffushi School"
                      data-testid="box-name-input"
                    />
                  </div>
                  <div>
                    <Label>Description (Optional)</Label>
                    <Input
                      value={newBox.description}
                      onChange={(e) => setNewBox({ ...newBox, description: e.target.value })}
                      placeholder="e.g., North section voters"
                    />
                  </div>
                  <Button onClick={handleCreateBox} className="w-full" data-testid="submit-create-box">
                    Create Box
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Overall Stats */}
            {statsData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">Total Registered</p>
                        <p className="text-3xl font-bold text-blue-900">{statsData.total_registered.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-green-700">Total Voted</p>
                        <p className="text-3xl font-bold text-green-900">{statsData.total_voted.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <Vote className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-purple-700">Turnout</p>
                        <p className="text-3xl font-bold text-purple-900">{statsData.turnout_percentage}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-orange-700">Unassigned</p>
                        <p className="text-3xl font-bold text-orange-900">{statsData.unassigned_count.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Voting Day & Times Info */}
            {statsData && (statsData.voting_day || statsData.voting_start_time || statsData.voting_end_time) && (
              <Card className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-center gap-6">
                    {statsData.voting_day && (
                      <div className="flex items-center gap-2">
                        <Vote className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium text-indigo-900">
                          Voting Day: {new Date(statsData.voting_day).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    {(statsData.voting_start_time || statsData.voting_end_time) && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium text-indigo-900">
                          Voting Hours: {statsData.voting_start_time && formatTime(statsData.voting_start_time)}
                          {statsData.voting_start_time && statsData.voting_end_time && ' - '}
                          {statsData.voting_end_time && formatTime(statsData.voting_end_time)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Public Dashboard Link */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Public Dashboard Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                {publicLinkData?.enabled ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <code className="flex-1 text-sm truncate">
                        {window.location.origin}{publicLinkData.public_url}
                      </code>
                      <Button size="sm" variant="ghost" onClick={copyPublicLink}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => window.open(publicLinkData.public_url, '_blank')}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleGeneratePublicLink}>
                        Regenerate Link
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleRemovePublicLink}>
                        <Unlink className="h-4 w-4 mr-2" />
                        Disable
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only people with this link can view the public turnout dashboard
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-muted-foreground">
                      Generate a secure link to share the turnout dashboard publicly
                    </p>
                    <Button onClick={handleGeneratePublicLink}>
                      <Link className="h-4 w-4 mr-2" />
                      Generate Public Link
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Voting Boxes */}
            <h2 className="text-2xl font-semibold mb-4">Voting Boxes</h2>
            {boxesData.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Vote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No voting boxes yet</h3>
                  <p className="text-muted-foreground mb-4">Create voting boxes manually to manage voter check-ins</p>
                  <p className="text-sm text-muted-foreground">Box names should match the "Vote Box" column values in constituent records</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {boxesData.map(box => (
                  <Card key={box.box_id} className="hover:shadow-lg transition-shadow" data-testid="voting-box-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">
                          {box.name}
                          {(box.manual_registered !== undefined && box.manual_registered !== null) && (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">Manual</span>
                          )}
                        </CardTitle>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openVotersDialog(box)}
                            title="View Voters"
                          >
                            <List className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditBoxDialog(box)}
                            title="Edit Counts"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {canAssignUsers && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openAssignUsersDialog(box)}
                              title="Assign Users"
                            >
                              <UserCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteBox(box.box_id)}
                            className="text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {box.description && (
                        <p className="text-sm text-muted-foreground">{box.description}</p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Voted / Registered */}
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-blue-700">Voted / Registered</span>
                            <span className="font-bold text-blue-900">
                              {box.voted?.toLocaleString() || 0} / {box.registered?.toLocaleString() || 0}
                            </span>
                          </div>
                          <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 transition-all duration-500"
                              style={{ width: `${box.registered > 0 ? (box.voted / box.registered) * 100 : 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-center text-blue-600 mt-1">
                            {box.registered > 0 ? ((box.voted / box.registered) * 100).toFixed(1) : 0}% turnout
                          </p>
                        </div>
                        
                        {/* Pledgers Voted / Pledges Registered */}
                        <div className="p-3 bg-emerald-50 rounded-lg">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-emerald-700">Pledgers Voted / Pledges</span>
                            <span className="font-bold text-emerald-900">
                              {box.pledgers_voted?.toLocaleString() || 0} / {box.pledges_registered?.toLocaleString() || 0}
                            </span>
                          </div>
                          <div className="h-2 bg-emerald-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 transition-all duration-500"
                              style={{ width: `${box.pledges_registered > 0 ? (box.pledgers_voted / box.pledges_registered) * 100 : 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-center text-emerald-600 mt-1">
                            {box.pledges_registered > 0 ? ((box.pledgers_voted / box.pledges_registered) * 100).toFixed(1) : 0}% pledge turnout
                          </p>
                        </div>
                        
                        <Button 
                          className="w-full mt-2" 
                          onClick={() => openCheckInDialog(box)}
                          data-testid="check-in-btn"
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Check In Voter
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Recent Votes */}
            {statsData?.recent_votes?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Check-ins
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {statsData.recent_votes.map((vote, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div>
                          <p className="font-medium">{vote.name}</p>
                          <p className="text-sm text-muted-foreground">{vote.id_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">{vote.box_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {vote.voted_at && new Date(vote.voted_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Assign Blocks Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Assign Blocks to {selectedBox?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select blocks to assign constituents to this voting box.
              </p>
              <div className="max-h-60 overflow-y-auto border rounded-lg p-2">
                {availableBlocks.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableBlocks.map(block => (
                      <button
                        key={block}
                        onClick={() => toggleBlock(block)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          selectedBlocks.includes(block)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-white hover:bg-muted border-border'
                        }`}
                      >
                        {block}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No blocks available</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAssignBlocks} className="flex-1" disabled={selectedBlocks.length === 0}>
                  Assign {selectedBlocks.length} Blocks
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => selectedBox && handleAssignUnassigned(selectedBox.box_id)}
                >
                  Assign Unassigned
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Check-in Dialog */}
        <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Voter Check-in - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>ID Card Number</Label>
                <Input
                  value={checkInId}
                  onChange={(e) => setCheckInId(e.target.value.toUpperCase())}
                  placeholder="A######"
                  className="text-lg font-mono"
                  onKeyDown={(e) => e.key === 'Enter' && handleCheckIn()}
                  autoFocus
                  data-testid="check-in-id-input"
                />
              </div>

              <Button
                className="w-full h-11 text-base"
                onClick={handleCheckIn}
                disabled={checkInLoading}
                data-testid="submit-check-in"
              >
                {checkInLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <UserCheck className="h-5 w-5 mr-2" />
                    Mark as Voted
                  </>
                )}
              </Button>

              {checkInResult && (
                <div className={`p-4 rounded-lg ${
                  checkInResult.success 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {checkInResult.success ? (
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800">{checkInResult.message}</p>
                        {checkInResult.constituent && (
                          <div className="mt-2 text-sm text-green-700">
                            <p><strong>Name:</strong> {checkInResult.constituent.name}</p>
                            <p><strong>ID:</strong> {checkInResult.constituent.id_number}</p>
                            <p><strong>Block:</strong> {checkInResult.constituent.block_number}</p>
                            {checkInResult.constituent.registered_box && (
                              <p><strong>Registered to box:</strong> {checkInResult.constituent.registered_box}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="font-medium text-red-800">{checkInResult.message}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Box Stats: {selectedBox?.voted || 0} / {selectedBox?.registered || 0} voted
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Voters Dialog */}
        <Dialog open={showVotersDialog} onOpenChange={setShowVotersDialog}>
          <DialogContent className="max-w-2xl h-[85vh] min-h-[500px] top-[5%] translate-y-0 flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Voters - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <div className="mt-2 ml-2 mb-3 flex items-center gap-2">
                <Input
                  placeholder="Search by ID"
                  value={boxVotersSearch}
                  onChange={(e) => handleBoxVotersSearchChange(e.target.value)}
                  className="max-w-xs font-mono"
                />
                {boxVotersLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent flex-shrink-0" />
                )}
              </div>
              {boxVotersLoading && boxVoters.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              ) : (
                <>
                  <div className="overflow-auto flex-1 min-h-0 border rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Name</th>
                          <th className="text-left p-2 font-medium">ID</th>
                          <th className="text-left p-2 font-medium">Address</th>
                          <th className="text-center p-2 font-medium w-12">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boxVoters.map((v) => (
                          <tr key={v.record_id} className="border-b last:border-b-0 hover:bg-muted/50">
                            <td className="p-2">{v.name}</td>
                            <td className="p-2 font-mono">{v.id_number}</td>
                            <td className="p-2">{v.address || '—'}</td>
                            <td className="p-2 text-center align-middle">
                              {v.voted ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600 inline-block" title="Voted" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-muted-foreground opacity-40 inline-block" title="Not voted" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t mt-3">
                    <p className="text-sm text-muted-foreground">
                      {boxVotersTotal} voter{boxVotersTotal !== 1 ? 's' : ''} total
                    </p>
                    {boxVotersTotal > boxVotersPageSize && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={boxVotersPage <= 1}
                          onClick={() => loadBoxVotersPage(boxVotersPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="text-sm py-2 px-1">
                          Page {boxVotersPage} of {Math.ceil(boxVotersTotal / boxVotersPageSize)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={boxVotersPage >= Math.ceil(boxVotersTotal / boxVotersPageSize)}
                          onClick={() => loadBoxVotersPage(boxVotersPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Users Dialog */}
        <Dialog open={showAssignUsersDialog} onOpenChange={setShowAssignUsersDialog}>
          <DialogContent className="max-w-md top-[7%] translate-y-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCircle className="h-5 w-5" />
                Assign Users - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Filter users</Label>
                <Input
                  placeholder="Search by name or email..."
                  value={assignUsersSearch}
                  onChange={(e) => handleAssignUsersSearchChange(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="border rounded-md max-h-48 overflow-y-auto">
                  {filteredAssignableUsers.length === 0 ? (
                    !assignUsersSearch.trim() ? null : (
                    <p className="p-3 text-sm text-muted-foreground">
                      {assignUsersLoading ? 'Loading...' : 'No users match filter'}
                    </p>
                    )
                  ) : (
                    <div className="divide-y">
                      {filteredAssignableUsers.map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between p-2 hover:bg-muted/50">
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email} • {u.role}</p>
                          </div>
                          <Button
                            size="sm"
                            variant={u.already_assigned ? "secondary" : "default"}
                            disabled={u.already_assigned}
                            onClick={() => !u.already_assigned && handleAssignUser(u.user_id)}
                          >
                            {u.already_assigned ? 'Assigned' : 'Assign'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              <div>
                <Label className="mb-2 block">Assigned users ({assignedUsers.length})</Label>
                {assignedUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No users assigned to this box</p>
                ) : (
                  <div className="border rounded-md divide-y">
                    {assignedUsers.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between p-2">
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleUnassignUser(u.user_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Box Dialog */}
        <Dialog open={showEditBoxDialog} onOpenChange={setShowEditBoxDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="h-5 w-5" />
                Edit Counts - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Override auto-calculated counts with manual values. Leave empty to use auto-calculated values.
              </p>
              
              {/* Show auto-calculated values for reference */}
              {selectedBox && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p className="font-medium mb-2">Auto-calculated values:</p>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    <span>Registered: {selectedBox.calculated_registered ?? '—'}</span>
                    <span>Voted: {selectedBox.calculated_voted ?? '—'}</span>
                    <span>Pledges: {selectedBox.calculated_pledges_registered ?? '—'}</span>
                    <span>Pledgers Voted: {selectedBox.calculated_pledgers_voted ?? '—'}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Manual Registered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_registered}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_registered: e.target.value })}
                    placeholder="Auto"
                    data-testid="edit-manual-registered"
                  />
                </div>
                <div>
                  <Label>Manual Voted</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_voted}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_voted: e.target.value })}
                    placeholder="Auto"
                    data-testid="edit-manual-voted"
                  />
                </div>
                <div>
                  <Label>Pledges Registered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_pledges_registered}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_pledges_registered: e.target.value })}
                    placeholder="Auto"
                    data-testid="edit-manual-pledges-registered"
                  />
                </div>
                <div>
                  <Label>Pledgers Voted</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_pledgers_voted}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_pledgers_voted: e.target.value })}
                    placeholder="Auto"
                    data-testid="edit-manual-pledgers-voted"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpdateBox} className="flex-1" data-testid="submit-edit-box">
                  Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleResetBoxCounts}
                  title="Reset to auto-calculated values"
                  data-testid="reset-box-counts"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
